import { Stripe, StripeElements } from '@stripe/stripe-js';
import { PaymentsFlowType } from '@frontend/payments-stripe-wrapper';
import { BaseFieldProps, contextFactory } from '@frontend/design-system';
import { StripeElementsConfig } from '../../../components/stripe/stripe-elements-config';
import { usePlatformComponentContext } from '../../../contexts/platform-component.context';
import { useProcessorDataContext } from '../../../contexts/processor-data.context';
import { CardOnFileBaseContext, CardOnFileProviderProps } from '../context/card-on-file.context';
import { useCardOnFileStripe } from '../hooks/use-card-on-file-stripe';
import { useCardOnFileStripeApi } from '../hooks/use-card-on-file-stripe-api';

export type CardOnFileStripeContextValue = CardOnFileBaseContext & {
  cardReadyToSubmit: boolean;
  setCardReadyToSubmit: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmitting: boolean;
  isSetupLinkLoading: boolean;
  isFetchedLinkLoading: boolean;
  saveCardOnFile: () => Promise<void>;
  stripe: Stripe | null;
  elements: StripeElements | null;
  nameFieldProps: BaseFieldProps<string, '', HTMLInputElement>;
};

export type CardOnFileStripeProviderBodyProps = CardOnFileProviderProps;

type StripeElementsWrapperProps = {
  type?: PaymentsFlowType;
  paymentConfigName?: string;
  disableAch?: boolean;
};

export type CardOnFileStripeProviderProps = CardOnFileStripeProviderBodyProps & StripeElementsWrapperProps;

export const [CardOnFileStripeContext, useCardOnFileStripeContext] = contextFactory<CardOnFileStripeContextValue>(
  '',
  {} as CardOnFileStripeContextValue
);

export const CardOnFileStripeProvider = ({
  children,
  type = PaymentsFlowType.AddCardOnFile,
  paymentConfigName = 'cardOnly',
  disableAch = true,
  ...props
}: CardOnFileStripeProviderProps) => {
  const { isPublic, locationId } = usePlatformComponentContext();
  const {
    activeProcessor,
    merchantIsFetched,
    locationData,
    stripeData: { enableAchOnFile },
  } = useProcessorDataContext();

  return (
    <StripeElementsConfig
      locationId={locationId}
      isPublic={isPublic}
      activeProcessor={activeProcessor}
      merchantIsFetched={merchantIsFetched}
      locationData={locationData}
      enableAchOnFile={enableAchOnFile}
      type={type}
      paymentConfigName={paymentConfigName}
      disableAch={disableAch}
    >
      <CardOnFileStripeProviderBody {...props}>{children}</CardOnFileStripeProviderBody>
    </StripeElementsConfig>
  );
};

const CardOnFileStripeProviderBody = ({ children, ...props }: CardOnFileStripeProviderBodyProps) => {
  const { locationId } = usePlatformComponentContext();

  const api = useCardOnFileStripeApi({ isPublic: !!props.isPublic });

  const value = useCardOnFileStripe({ ...props, locationId, api });

  return <CardOnFileStripeContext.Provider value={value}>{children}</CardOnFileStripeContext.Provider>;
};
